import { Component, Input, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ModalController, ToastController } from '@ionic/angular';
import { ClientConfigurationService } from 'src/app/core/client-configuration/client-configuration.service';
import { ConfigSettings } from 'src/app/core/client-configuration/config-settings.enum';
import { FormHelperService } from 'src/app/core/helpers/form-helper/form-helper.service';
import { AppointmentInventory } from 'src/app/core/models/appointment-inventory.model';
import { InventoryRemovalReason, InventoryRemovalUpdate, InventoryStatus } from 'src/app/core/models/inventory.model';
import { InventoryService } from 'src/app/core/services/inventory/inventory.service';
import { TicketProvisioningService } from 'src/app/core/services/ticket-provisioning/ticket-provisioning.service';
import { AddTruckInventoryModalComponent } from '../add-truck-inventory-modal/add-truck-inventory-modal.component';
import { AppointmentService } from 'src/app/core/services/appointment/appointment.service';
import { OverlayEventDetail } from '@ionic/core';
import { AddWarehouseInventoryModalComponent } from '../add-warehouse-inventory-modal/add-warehouse-inventory-modal.component';

type AddInventoryModalResponse = OverlayEventDetail<{ forcedModal?: string, isSwap?: boolean }>;

@Component({
	selector: 'app-remove-inventory',
	templateUrl: './remove-inventory.component.html',
	styleUrls: ['./remove-inventory.component.scss'],
})
export class RemoveInventoryComponent implements OnInit {

	pageTitle = 'Remove Inventory';

	@Input()
	inventoryItem: AppointmentInventory;

	@Input()
	appointmentId: number;

	form: FormGroup;
	enableInventoryRemovalReason: boolean;
	removalStatusOptions: InventoryStatus[] = [];
	removalReasons: InventoryRemovalReason[] = [];

	constructor(
		private modalController: ModalController,
		private clientConfig: ClientConfigurationService,
		private inventoryService: InventoryService,
		private formBuilder: FormBuilder,
		public formHelper: FormHelperService,
		private toastController: ToastController,
		private ticketProvisioningService: TicketProvisioningService,
		private appointmentService: AppointmentService,
	) { }

	async ngOnInit() {
		this.removalStatusOptions = await this.inventoryService.getRemovalStatuses();

		const formFields = {
			removalStatus: [this.initRemovalStatus(), Validators.required]
		} as { removalStatus: any[], removalReason?: any[], removalNote?: any[] };

		this.enableInventoryRemovalReason = (await this.clientConfig.getSetting(ConfigSettings.Enable_Inventory_Removal_Reason))
			?.value === 'True';
		if (this.enableInventoryRemovalReason) {
			this.removalReasons = await this.inventoryService.getRemovalReasons();
			formFields.removalReason = [this.initRemovalReason(), Validators.required];
			formFields.removalNote = ['', Validators.compose([Validators.required, Validators.minLength(5)])];
		}

		this.form = this.formBuilder.group(formFields);
	}

	async onRemoveInventory() {
		this.form = this.formHelper.markAsTouched(this.form);
		if (this.form.invalid) {
			await this.formHelper.alertUser();
			return;
		}

		await this.inventoryService.removeInventoryHttp(this.appointmentId, this.inventoryItem.id, this.form.value);
		await this.inventoryService.removeInventoryLocal(this.appointmentId, this.inventoryItem.id, this.form.value);
		await this.ticketProvisioningService.addProvision(this.appointmentId, this.inventoryItem);

		await this.toastRemoveSuccess();
		await this.dismiss(this.form.value);
	}

	async dismiss(removalUpdates?: InventoryRemovalUpdate): Promise<void> {
		await this.modalController.dismiss({
			dismissed: true
		});
	}

	private initRemovalReason(): any {
		return this.removalReasons.length == 0 ? -1 : null;
	}

	private initRemovalStatus() {
		if (this.removalStatusOptions.length == 1) {
			return this.removalStatusOptions[0].id;
		}

		return this.removalStatusOptions.find(r => r.isDefaultRemovedStatus)?.id;
	}

	private async toastRemoveSuccess(): Promise<void> {
		const toast = await this.toastController.create({
			message: 'Successfully removed inventory.',
			duration: 3000,
			position: 'top',
			color: 'success',
			buttons: ['Dismiss'],
		});

		await toast.present();
	}

	async onSwapInventory() {
		this.form = this.formHelper.markAsTouched(this.form);
		if (this.form.invalid) {
			await this.formHelper.alertUser();
			return;
		}

		await this.toastSwapping();
		await this.openAddInventory();

		await this.dismiss(this.form.value);
	}

	private async toastSwapping(): Promise<void> {
		const toast = await this.toastController.create({
			message: 'Swapping inventory. Choose replacement inventory.',
			duration: 3000,
			position: 'top',
			color: 'success',
			buttons: ['Dismiss'],
		});

		await toast.present();
	}
	private async toastSwapSuccess(): Promise<void> {
		const toast = await this.toastController.create({
			message: 'Successfully swapped inventory.',
			duration: 3000,
			position: 'top',
			color: 'success',
			buttons: ['Dismiss'],
		});

		await toast.present();
	}

	async openAddInventory(forcedModal?: string): Promise<void> {
		const modalConfig = await this.inventoryService.getAddInventoryModalConfig();
		const component = (!forcedModal && modalConfig.useTruck) || forcedModal === 'TRUCK'
			? AddTruckInventoryModalComponent
			: AddWarehouseInventoryModalComponent;
		const appointment = await this.appointmentService.getLocalAppointment(this.appointmentId);
		const modal = await this.modalController.create({
			component,
			componentProps: {
				appointment: appointment,
				prefillOptions: {
					categoryId: this.inventoryItem.categoryId,
					equipmentId: this.inventoryItem.equipId,
					customerServiceId: this.inventoryItem.customerServiceId
				},
				isSwap: true,
				...modalConfig,
			},
			cssClass: 'full-screen-modal',
		});

		await modal.present();
		await modal.onDidDismiss().then(
			async (response: AddInventoryModalResponse) => {
				if (response.data.forcedModal) {
					await this.openAddInventory(response.data.forcedModal);
				}
				if (response.data.isSwap) {
					await this.inventoryService.removeInventoryHttp(this.appointmentId, this.inventoryItem.id, this.form.value);
					await this.inventoryService.removeInventoryLocal(this.appointmentId, this.inventoryItem.id, this.form.value);
					await this.ticketProvisioningService.addProvision(this.appointmentId, this.inventoryItem);
					await this.toastSwapSuccess();
               }
			}
		);
	}


}
